import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Check } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import { http, trackEvent } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  ["newborn", "Newborn Sleep", ["newborn"]],
  ["safe_sleep", "Safe Sleep & Sleep Environment", ["safe_sleep"]],
  ["sleep_problems", "Baby Sleep Problems & Solutions", ["sleep_problems", "night_wakings", "naps", "bedtime", "crib", "regressions"]],
  ["toddler", "Toddler Sleep", ["toddler"]],
  ["family_courses", "Family Sleep & Parent Education", ["family_courses", "age", "schedules", "commercial"]],
];
const LABELS = { commercial: "Understanding Professional Support", night_wakings: "Night Wakings", naps: "Naps", crib: "Crib Sleep",
  bedtime: "Bedtime", regressions: "Regressions", age: "Sleep by Age", schedules: "Schedules", buying_intent: "Understanding Professional Support",
  newborn: "Newborn Sleep", safe_sleep: "Safe Sleep & Sleep Environment", sleep_problems: "Baby Sleep Problems & Solutions",
  toddler: "Toddler Sleep", family_courses: "Family Sleep & Parent Education" };

function LeadMagnet({ resource, title, desc, compact }) {
  const [f, setF] = useState({ first_name: "", email: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await http.post("/leads/resource", { ...f, resource }); setResult(data); trackEvent("lead_created", { resource, source: "resource_library" }); }
    catch (e2) {} finally { setBusy(false); }
  };
  if (result) return (
    <div className="card-night rounded-xl p-6" data-testid={`lead-result-${resource}`}>
      <div className="eyebrow text-[#C2836B] mb-2">Your free {result.title}</div>
      <ul className="space-y-2">{result.body.map((b) => <li key={b} className="flex gap-2 text-sm text-[#A39E98]"><Check size={15} className="text-[#738075] mt-0.5 shrink-0" /> {b}</li>)}</ul>
      <p className="text-[#738075] text-xs mt-4">Keep these observations in the free Sleep Observation Tracker included with every student account.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="card-night rounded-xl p-6" data-testid={`lead-form-${resource}`}>
      <div className="eyebrow text-[#C2836B] mb-1">Free mini-resource</div>
      <h3 className="font-display text-xl text-[#F7F5F0]">{title}</h3>
      <p className="text-[#A39E98] text-sm mt-1 mb-4">{desc}</p>
      <div className={`flex flex-col ${compact ? "sm:flex-row" : "sm:flex-row"} gap-2`}>
        <input required placeholder="First name" data-testid={`lead-name-${resource}`} value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="flex-1 bg-[#1C1B1A] border border-[#3a3835] rounded-lg px-3 py-2.5 text-[#F7F5F0] text-sm" />
        <input required type="email" placeholder="Email" data-testid={`lead-email-${resource}`} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="flex-1 bg-[#1C1B1A] border border-[#3a3835] rounded-lg px-3 py-2.5 text-[#F7F5F0] text-sm" />
        <button disabled={busy} data-testid={`lead-submit-${resource}`} className="btn-clay rounded-full px-5 py-2.5 text-sm whitespace-nowrap">{busy ? "…" : "Get it free"}</button>
      </div>
    </form>
  );
}

// A relevant free checklist wired into each library cluster.
const LEAD_BY_CLUSTER = {
  naps: { resource: "short-nap-checklist", title: "Short Nap Checklist", desc: "Track naps for a few days and spot what's really going on." },
  bedtime: { resource: "bedtime-troubleshooting-guide", title: "Bedtime Troubleshooting Guide", desc: "Simple checks when bedtime has become a battle." },
  crib: { resource: "crib-sleep-observation-guide", title: "Crib Sleep Observation Guide", desc: "Gentle steps to observe how crib sleep is going." },
  regressions: { resource: "4-month-sleep-changes-guide", title: "4-Month Sleep Changes Guide", desc: "Understand the permanent change in sleep around 4 months." },
};

export default function Resources() {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [preview, setPreview] = useState({ categories: [], modules: [] });
  const [sel, setSel] = useState(null);
  useSeo({ title: "Infant Sleep Library | Guides on Naps, Night Wakings & More", description: "The Infant Sleep Library: professionally reviewed guides on night wakings, naps, bedtime, crib sleep, regressions, sleep by age, and schedules.", canonical: "/resources" });
  useEffect(() => {
    http.get("/content").then((r) => setPages(r.data)).catch(() => {});
    http.get("/modules/preview").then((r) => setPreview(r.data)).catch(() => {});
  }, []);

  const byCluster = {};
  pages.forEach((p) => { const k = p.cluster === "buying_intent" ? "commercial" : p.cluster; (byCluster[k] = byCluster[k] || []).push(p); });
  const cats = CATEGORIES.map(([id, label, subs]) => [id, label, subs.filter((s) => byCluster[s])]).filter(([, , subs]) => subs.length);

  const freeMods = preview.modules.filter((m) => m.tier === "clarity");

  const PreviewCard = ({ m }) => (
    <div role="button" tabIndex={0} onClick={() => setSel(m)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSel(m); } }}
      data-testid={`preview-module-${m.key}`}
      className="card-night rounded-lg overflow-hidden text-left group hover:border-[#C2836B]/50 transition-colors cursor-pointer">
      {m.image && (
        <div className="relative h-24 w-full overflow-hidden">
          <img src={m.image} alt="" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#161514] to-transparent" />
          <div className="absolute top-2 right-2 bg-[#738075]/90 backdrop-blur rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide text-white">IS-101</div>
        </div>
      )}
      <div className="p-4">
        <h3 className="text-[#F7F5F0] text-[15px] leading-snug">{m.title}</h3>
        <p className="text-[#A39E98] text-sm mt-1">{m.summary}</p>
      </div>
    </div>
  );

  return (
    <MarketingShell>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-8">
        <div className="eyebrow text-[#C2836B] mb-4">Free Sleep Guides</div>
        <h1 className="font-display text-4xl sm:text-5xl text-[#F7F5F0] tracking-tight max-w-3xl" data-testid="resources-h1">Free Baby & Toddler Sleep Guides</h1>
        <div className="text-[#A39E98] mt-5 max-w-2xl text-[17px] leading-relaxed space-y-3" data-testid="resources-intro">
          <p>Some nights, you don't need another complicated plan. You just need someone to explain what might be happening and where to begin.</p>
          <p>Explore practical newborn, infant, and toddler sleep guidance, written from Andrea Hurley's professional perspective. No registration or payment required.</p>
        </div>
        <nav className="mt-8 flex flex-wrap gap-2" aria-label="Guide categories" data-testid="resources-category-nav">
          {cats.map(([id, label, subs]) => (
            <a key={id} href={`#cat-${id}`} data-testid={`resources-jump-${id}`} className="rounded-full border border-[#C2836B]/40 px-4 py-2 text-sm text-[#F7F5F0] hover:bg-[#C2836B]/15 transition-colors">
              {label} <span className="text-[#738075]">({subs.reduce((n, s) => n + byCluster[s].length, 0)})</span>
            </a>
          ))}
        </nav>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16 space-y-14" data-testid="resources-categories">
        {cats.map(([id, label, subs]) => (
          <div key={id} id={`cat-${id}`} className="scroll-mt-24" data-testid={`resources-cat-${id}`}>
            <div className="flex items-baseline justify-between mb-5 border-b border-[#C2836B]/30 pb-3">
              <h2 className="font-display text-2xl sm:text-3xl text-[#F7F5F0]">{label}</h2>
              <span className="text-[#738075] text-sm">{subs.reduce((n, s) => n + byCluster[s].length, 0)} guides</span>
            </div>
            <div className="space-y-6">
              {subs.map((s) => (
                <div key={s}>
                  {subs.length > 1 && <h3 className="eyebrow text-[#C2836B] mb-3">{LABELS[s]}</h3>}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {byCluster[s].map((p) => (
                      <Link key={p.slug} to={`/${p.slug}`} data-testid={`resource-${p.slug}`}
                        className="card-night rounded-lg p-5 group hover:border-[#C2836B]/50 transition-colors flex items-center justify-between gap-3">
                        <span className="text-[#F7F5F0] text-[15px] leading-snug">{p.h1}</span>
                        <ArrowRight size={16} className="text-[#738075] group-hover:text-[#C2836B] group-hover:translate-x-1 transition-all shrink-0" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-6 border-t border-[#2E2C2A]" data-testid="resources-checklists">
        <h2 className="font-display text-2xl text-[#F7F5F0] mb-2">Free checklists</h2>
        <p className="text-[#A39E98] text-sm mb-6 max-w-xl">Short observation exercises to use alongside the guides above.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {Object.values(LEAD_BY_CLUSTER).map((l) => <LeadMagnet key={l.resource} compact {...l} />)}
        </div>
      </section>

      {!user && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-2" data-testid="resources-free-account-banner">
          <div className="card-night rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-[#C2836B]/25">
            <div>
              <div className="eyebrow text-[#C2836B] mb-1">Student admissions</div>
              <h2 className="font-display text-xl sm:text-2xl text-[#F7F5F0]">Create your free student account</h2>
              <p className="text-[#A39E98] text-sm mt-1 max-w-xl">No payment needed. Your free student account includes the Sleep Observation Tracker, saved observation history and the full Course Catalog.</p>
            </div>
            <div className="flex gap-3 shrink-0 items-center">
              <Link to="/register" data-testid="resources-create-account" className="btn-clay rounded-full px-5 py-2.5 text-sm whitespace-nowrap">Create free account</Link>
              <Link to="/login" className="text-sm text-[#A39E98] hover:text-[#F7F5F0]">Sign in</Link>
            </div>
          </div>
        </section>
      )}

      {/* IS-101 lesson preview (free course lessons only) */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-20">
        <div className="grid lg:grid-cols-[2fr_1fr] gap-6 items-start">
          <div>
            <div className="flex items-center gap-2 mb-2"><BookOpen size={16} className="text-[#C2836B]" /><h2 className="font-display text-2xl text-[#F7F5F0]">IS-101 lessons in the Learning Center</h2></div>
            <p className="text-[#A39E98] text-sm mb-6 max-w-xl">The lessons of IS-101 Foundations of Infant Sleep, a self-paced course ($99 tuition, now open for enrollment). Select a lesson to preview it.</p>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="module-preview">
              {freeMods.map((m) => <PreviewCard key={m.key} m={m} />)}
            </div>
          </div>
          <LeadMagnet resource="night-waking-checklist" title="Night Waking Checklist" desc="A short observation exercise to accompany the IS-101 lesson on night wakings." />
        </div>
      </section>

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setSel(null)} data-testid="preview-module-dialog">
          <div className="bg-[#161514] border border-[#2E2C2A] rounded-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {sel.image && (
              <div className="relative h-36 w-full overflow-hidden">
                <img src={sel.image} alt="" className="w-full h-full object-cover opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#161514] to-transparent" />
              </div>
            )}
            <div className="p-7">
              <div className="eyebrow text-[#738075] mb-1 flex items-center gap-1.5"><BookOpen size={11} /> IS-101 lesson</div>
              <h2 className="font-display text-2xl text-[#F7F5F0]">{sel.title}</h2>
              <p className="text-[#A39E98] mt-3 text-[15px] leading-relaxed">{sel.sample}</p>
              <p className="text-[#738075] text-sm mt-4">What this covers: {sel.summary}</p>
              <p className="text-[#6f6a64] text-xs mt-4 leading-relaxed">The full lesson opens in the Learning Center with a free student account.</p>
              <div className="mt-6 flex items-center gap-3">
                <Link to="/courses/is-101" data-testid="preview-module-cta" className="btn-clay rounded-full px-5 py-2.5 text-sm whitespace-nowrap">View IS-101 Tuition & Syllabus</Link>
                <button onClick={() => setSel(null)} className="text-sm text-[#A39E98]">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </MarketingShell>
  );
}
